import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import CreateGIBDDForm from "~/components/Forms/CreateGIBDDForm";
import GIBDDUploadFileForm from "~/components/Forms/GIBDDUploadFileForm";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { useGetGibddQuery } from "~/schemas/generated/graphql";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import GIBDDFilter from "./Filter";
import { getGibddColumns, GIBDD_SORTABLE_COLUMNS } from "./model/tableAdapter";
import { OPTIONS_GIBDD } from "./options";

// Старая getSelectValuesCity/newStr: город уходит в API
// с заглавной первой буквы, остальные — в нижнем регистре
const capitalizeCity = (str: string) =>
  str[0].toUpperCase() + str.slice(1).toLowerCase();

const GIBDD = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const {
    openModal,
    count,
    page,
    currentId,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const { id, vin, dateRegistrationStart, dateRegistrationEnd, city } =
    useGetLocationData() as Record<string, unknown>;

  // Бэкенд SRRGetter.getGIBDD не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: GIBDD_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && GIBDD_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const cityList = city
    ? String(city).split(",").filter(Boolean).map(capitalizeCity)
    : undefined;

  const { data, loading, refetch } = useGetGibddQuery({
    variables: {
      page,
      count,
      id: id ? String(id) : undefined,
      vin: vin ? String(vin) : undefined,
      dateRegistrationStart: dateRegistrationStart
        ? new Date(String(dateRegistrationStart))
        : undefined,
      dateRegistrationEnd: dateRegistrationEnd
        ? new Date(String(dateRegistrationEnd))
        : undefined,
      city: cityList && cityList.length > 0 ? cityList : undefined,
    },
    fetchPolicy: "no-cache",
  });

  const tableRows = get(data, "SRRGetter.getGIBDD.items", []).map((el) => ({
    ...el,
    // raw-поля для клиентской сортировки по timestamp
    createdAtRaw: el.createdAt ? new Date(el.createdAt).getTime() : 0,
    updatedAtRaw: el.updatedAt ? new Date(el.updatedAt).getTime() : 0,
    dateRegistrationRaw: el.dateRegistration
      ? new Date(el.dateRegistration).getTime()
      : 0,
    createdAt: getFormatDate(el.createdAt, true),
    updatedAt: getFormatDate(el.updatedAt, true),
    dateRegistration: getFormatDate(el.dateRegistration, true),
    dateRegistrationForm: el.dateRegistration,
  }));
  const total = get(data, "SRRGetter.getGIBDD.total", 0);

  // currentId из URL — строка, id из GraphQL (scalar ID) — тоже строка
  const selectedGibdd = tableRows.find((i) => i.id === currentId);

  const { handleCount, handlePage } = usePaginationParams();

  const goBackHandler = () => {
    history.replace("/srr/");
  };

  const openAddModal = () => setDataReplace("openModal", "Add");

  const handleRowEdit = (record: { id: string | number }) => {
    setDataReplace("openModal", "EditTable", "currentId", record.id);
  };

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "currentId", "", "fileId", "");
  };

  const columns = getGibddColumns(t, OPTIONS_GIBDD, tableSortField, sortType);

  return (
    <Wrapper.Content
      title={"Регистрация автомобилей по данным ГИБДД"}
      txtBtn={t("ui.back")}
      goBackHandler={goBackHandler}
      filterComponent={<GIBDDFilter />}
      controlComponent={
        <Button
          onClick={openAddModal}
          size={"middle"}
          icon={<PlusIcon size={16} />}
        >
          {t("ui.upload")}
        </Button>
      }
    >
      <TableV2
        columns={columns}
        dataSource={tableRows}
        rowKey="id"
        loading={loading}
        onRowEdit={handleRowEdit}
        actionsFixed
        sortableColumns={GIBDD_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        open={openModal === "Add"}
        title={t("pkd.upload_file")}
        onCancel={handleCloseModal}
        closable
        centered
      >
        <GIBDDUploadFileForm setCloseModal={handleCloseModal} />
      </Modal>
      {!!selectedGibdd && (
        <Modal
          open={openModal === "EditTable"}
          title={t("gibdd.EditTable")}
          onCancel={handleCloseModal}
          closable
          centered
        >
          <CreateGIBDDForm
            gibdd={selectedGibdd}
            setCloseModal={handleCloseModal}
            refetch={refetch}
          />
        </Modal>
      )}
    </Wrapper.Content>
  );
};

export default GIBDD;
